
import React, { createContext, useContext, ReactNode, useEffect } from "react";
import { PRODUCTION_CONFIG } from '@/config/production';

interface NotificationContextType {
  isInitialized: boolean;
  initializationError: string | null;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotificationContext must be used within a NotificationProvider");
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  // Use a simple static value instead of useState to avoid dispatcher issues
  const value: NotificationContextType = {
    isInitialized: typeof window !== 'undefined' && 'Notification' in window,
    initializationError: typeof window !== 'undefined' && !('Notification' in window) 
      ? "Browser does not support notifications" 
      : null
  };

  // Log initialization without using hooks
  useEffect(() => {
    if (PRODUCTION_CONFIG.monitoring.enableConsoleLogging) {
      console.log("Notification provider initialized - supported:", value.isInitialized);
    }
  }, []);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
